import Sentry from '@/services/sentry'
import Vue from 'vue'
import slideTypeCsatApi from '@/api/slideTypeCsat'
import Util from '@/util'

// BAY-4: per-slide-type CSAT ratings for the scalable slide-type picker's
// Rating cell. Fetched at most ONCE per page load: every picker / details-
// modal mount dispatches `fetchSlideTypesCsat`, but only the first call hits
// the network — later calls share the in-flight promise or return
// immediately once it settled. A failed request is not retried: the rating
// getter returns null and `areRatingsLoaded` stays false, so callers keep the
// Rating cell hidden and a broken endpoint is never hammered on each open.

const SET_RATINGS = 'SET_RATINGS'
const SET_GRADUATED = 'SET_GRADUATED'
const SET_SETTLED = 'SET_SETTLED'
const SET_FETCH_PROMISE = 'SET_FETCH_PROMISE'

const toRating = (entry) => {
  if (!entry) return null
  const stars = Number(entry.stars)
  const ratingCount = Number(entry.ratingCount)
  if (!Number.isFinite(stars) || !Number.isFinite(ratingCount) || ratingCount <= 0) return null
  return {
    stars: Math.min(Math.max(stars, 0), 5),
    csatPercent: Number(entry.csatPercent) || 0,
    ratingCount,
  }
}

const _state = {
  // { [slideType]: { stars, csatPercent, ratingCount } }
  ratings: null,
  graduated: {},
  settled: false,
  // Set on the first dispatch and never cleared — this is the "only once" guard.
  fetchPromise: null,
}

const _getters = {
  areRatingsLoaded: (state) => state.ratings !== null,
  // Lookup a rating by (type, slideType). Native quiz variants all share
  // `type='pickAnswer'` and are discriminated by `slideType`, so a lookup
  // on `type` alone returns the same rating for every variant. Normalize
  // the Pick Answer family through `Util.getCorrectSlideTypeName` and key
  // on `pickAnswer::${variant}`; fall back to the raw type for every
  // other entry (marketplace / native base types keyed by `type` only).
  isCsatSettled: (state) => state.settled,
  isSlideTypeGraduated: (state) => (type, slideType) => {
    if (!type) return false
    const key = type === 'pickAnswer'
      ? `pickAnswer::${Util.getCorrectSlideTypeName(type, slideType || null)}`
      : type
    return state.graduated[key] === true
  },
  slideTypeRating: (state) => (type, slideType) => {
    if (!type || !state.ratings) return null
    if (type === 'pickAnswer') {
      const variant = Util.getCorrectSlideTypeName(type, slideType || null)
      return state.ratings[`pickAnswer::${variant}`] || null
    }
    return state.ratings[type] || null
  },
}

const mutations = {
  [SET_SETTLED](state) {
    Vue.set(state, 'settled', true)
  },
  [SET_GRADUATED](state, graduated) {
    Vue.set(state, 'graduated', graduated)
  },
  [SET_RATINGS](state, ratings) {
    Vue.set(state, 'ratings', ratings)
  },
  [SET_FETCH_PROMISE](state, promise) {
    Vue.set(state, 'fetchPromise', promise)
  },
}

const actions = {
  fetchSlideTypesCsat({ commit, state, rootGetters }) {
    const isEnabled = rootGetters['featureToggles/canUseScalableSlideTypePicker'] ||
      rootGetters['featureToggles/canUseSlideTypePickerSort'] ||
      rootGetters['featureToggles/canUseSlideTypeTrustBadges']
    if (!isEnabled) return Promise.resolve()
    if (state.fetchPromise) return state.fetchPromise

    const promise = slideTypeCsatApi.getSlideTypesCsat()
      .then((body) => {
        const raw = (body && body.data) || {}
        const ratings = Object.keys(raw).reduce((acc, type) => {
          const rating = toRating(raw[type])
          if (rating) acc[type] = rating
          return acc
        }, {})
        commit(SET_GRADUATED, Object.keys(raw).reduce((acc, type) => {
          if (raw[type] && raw[type].graduated === true) acc[type] = true
          return acc
        }, {}))
        commit(SET_RATINGS, ratings)
        commit(SET_SETTLED)
        return ratings
      })
      .catch((error) => {
        commit(SET_SETTLED)
        Sentry.captureException(error)
      })
    commit(SET_FETCH_PROMISE, promise)
    return promise
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
